/**
 * Web-Audio cat-themed sound effects, synthesized on the fly.
 *
 * No external assets — every sound is built from oscillators, white noise
 * and biquad filters at play time. That keeps the bundle tiny and lets us
 * tune the vibe by tweaking parameters instead of hunting for new files.
 *
 * All sounds are short (~150–800ms) and lightly mixed (peak gain 0.3) so
 * they don't drown out the player's music if any.
 *
 * Lazy-init: the `AudioContext` is created on the first `play*()` call.
 * Browsers require a user gesture before audio can play, so we resume
 * the context if it was suspended.
 */

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
/** Volume multiplier applied to every sound (0..1). Persisted to
 *  localStorage so the player's last setting survives reloads. Default is
 *  intentionally low (0.30) — players can crank it up via the settings
 *  panel; a quiet default avoids blasting new visitors. */
let masterVolume: number = (() => {
  if (typeof window === 'undefined') return 0.30;
  try {
    const v = window.localStorage.getItem('wol.volume.sfx');
    if (v !== null) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
    }
  } catch { /* localStorage disabled */ }
  return 0.30;
})();
/** Set to true to silence everything (could be wired to a UI toggle later). */
let muted = false;

export function getMasterVolume(): number { return masterVolume; }

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = (window as Window & {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    }).AudioContext
      ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try { ctx = new Ctor(); } catch { return null; }
    masterGain = ctx.createGain();
    masterGain.gain.value = masterVolume;
    masterGain.connect(ctx.destination);
  }
  // Browsers may suspend the context until user interaction. Resume safely.
  if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
  return ctx;
}

export function setMasterVolume(v: number): void {
  masterVolume = Math.max(0, Math.min(1, v));
  if (masterGain) masterGain.gain.value = muted ? 0 : masterVolume;
  try { window.localStorage.setItem('wol.volume.sfx', String(masterVolume)); }
  catch { /* localStorage disabled — preference just lives in memory */ }
}

export function setMuted(m: boolean): void {
  muted = m;
  if (masterGain) masterGain.gain.value = m ? 0 : masterVolume;
}

/** Per-key timestamp of the last time a given SFX fired. Used by
 *  `throttle()` below to drop a play() call when the same sound was
 *  triggered very recently — without this, simultaneous broadcast
 *  events (e.g. five armies dying at once, or a barbarian-turn that
 *  fires three moves in 2 s) stack their meows / ploufs into a
 *  garbled wall of noise. */
const lastPlayedAt = new Map<string, number>();

/** Returns `true` if the given key fired within `minMs`. The caller
 *  should bail out early (skip playing the SFX) if this is `true`.
 *  Updates the timestamp on the FALSE branch only — so re-trying
 *  within the throttle window doesn't extend the cooldown. */
function throttle(key: string, minMs: number): boolean {
  const now = performance.now();
  const last = lastPlayedAt.get(key) ?? -Infinity;
  if (now - last < minMs) return true;
  lastPlayedAt.set(key, now);
  return false;
}

/** Build a short-lived noise buffer used by hiss / whoosh sounds. */
function buildNoiseBuffer(ac: AudioContext, durationSec: number): AudioBuffer {
  const sampleRate = ac.sampleRate;
  const length = Math.floor(sampleRate * durationSec);
  const buf = ac.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** Convenience: schedule a gain envelope (ADSR-ish, simplified to AR). */
function envelope(
  ac: AudioContext,
  start: number,
  peak: number,
  attackSec: number,
  releaseSec: number,
): GainNode {
  const g = ac.createGain();
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(peak, start + attackSec);
  g.gain.linearRampToValueAtTime(0, start + attackSec + releaseSec);
  return g;
}

/**
 * "Meow" — used for army creation/recruitment. A rising-then-falling sine
 * sweep around 600–1200 Hz with a fast vibrato. Reads as "miaou !".
 */
export function playMeow(): void {
  if (throttle('meow', 200)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(600, t);
  osc.frequency.exponentialRampToValueAtTime(1200, t + 0.13);
  osc.frequency.exponentialRampToValueAtTime(700, t + 0.28);
  // Vibrato — modulate the carrier frequency.
  const lfo = ac.createOscillator();
  lfo.frequency.value = 18;
  const lfoGain = ac.createGain();
  lfoGain.gain.value = 35;
  lfo.connect(lfoGain);
  lfoGain.connect(osc.frequency);
  const env = envelope(ac, t, 0.35, 0.02, 0.27);
  osc.connect(env);
  env.connect(masterGain!);
  osc.start(t); lfo.start(t);
  osc.stop(t + 0.3); lfo.stop(t + 0.3);
}

/**
 * "Purr" — short, low rumble. Used for reinforcement (peaceful arrival)
 * and resource-collection feedback.
 */
export function playPurr(): void {
  if (throttle('purr', 250)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.value = 95;
  // Amplitude tremolo at ~25 Hz produces the rrrrr texture.
  const lfo = ac.createOscillator();
  lfo.frequency.value = 25;
  const lfoGain = ac.createGain();
  lfoGain.gain.value = 0.18;
  const baseGain = ac.createGain();
  baseGain.gain.value = 0.22;
  lfo.connect(lfoGain);
  lfoGain.connect(baseGain.gain);
  // Lowpass to soften the saw → more rumbly than buzzy.
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 600;
  const env = envelope(ac, t, 1, 0.04, 0.4);
  osc.connect(lp); lp.connect(baseGain); baseGain.connect(env); env.connect(masterGain!);
  osc.start(t); lfo.start(t);
  osc.stop(t + 0.46); lfo.stop(t + 0.46);
}

/**
 * "Whoosh + tap" — short noise burst with a downward sweep + a tonal
 * impact. Used for arrows / ranged shots.
 */
export function playWhoosh(): void {
  if (throttle('whoosh', 80)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Noise component
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.2);
  const bp = ac.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(3000, t);
  bp.frequency.exponentialRampToValueAtTime(800, t + 0.15);
  bp.Q.value = 4;
  const noiseEnv = envelope(ac, t, 0.4, 0.01, 0.18);
  noise.connect(bp); bp.connect(noiseEnv); noiseEnv.connect(masterGain!);
  // Tonal tap at the end
  const tap = ac.createOscillator();
  tap.type = 'triangle';
  tap.frequency.value = 380;
  const tapEnv = envelope(ac, t + 0.13, 0.25, 0.005, 0.08);
  tap.connect(tapEnv); tapEnv.connect(masterGain!);
  noise.start(t); tap.start(t + 0.13);
  noise.stop(t + 0.22); tap.stop(t + 0.22);
}

/**
 * "Hiss + bonk" — angry cat hiss followed by a percussive thwack. Used
 * for melee attacks.
 */
export function playHiss(): void {
  if (throttle('hiss', 200)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.18);
  const hp = ac.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 3500;
  const hissEnv = envelope(ac, t, 0.3, 0.01, 0.14);
  noise.connect(hp); hp.connect(hissEnv); hissEnv.connect(masterGain!);
  // Bonk: short square at low pitch
  const bonk = ac.createOscillator();
  bonk.type = 'square';
  bonk.frequency.setValueAtTime(220, t + 0.1);
  bonk.frequency.exponentialRampToValueAtTime(110, t + 0.16);
  const bonkEnv = envelope(ac, t + 0.1, 0.3, 0.005, 0.1);
  bonk.connect(bonkEnv); bonkEnv.connect(masterGain!);
  noise.start(t); bonk.start(t + 0.1);
  noise.stop(t + 0.18); bonk.stop(t + 0.21);
}

/**
 * "Fanfare" — three rising sine notes. Used for territory capture +
 * castle takeover.
 */
export function playFanfare(): void {
  if (throttle('fanfare', 400)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const notes = [600, 760, 1010];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.13;
    const env = envelope(ac, start, 0.3, 0.01, 0.2);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.22);
  }
}

/**
 * "Plouf" — descending square wave + decay. Used when a stack gets
 * annihilated.
 */
export function playPlouf(): void {
  if (throttle('plouf', 200)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(500, t);
  osc.frequency.exponentialRampToValueAtTime(110, t + 0.32);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1200;
  const env = envelope(ac, t, 0.25, 0.01, 0.32);
  osc.connect(lp); lp.connect(env); env.connect(masterGain!);
  osc.start(t); osc.stop(t + 0.34);
}

/**
 * "Tinkle" — two short bright notes. Used for tech research success.
 */
export function playTinkle(): void {
  if (throttle('tinkle', 150)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const notes = [1500, 2000];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.08;
    const env = envelope(ac, start, 0.25, 0.005, 0.14);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.16);
  }
}

/**
 * "Tap" — single soft click. UI feedback for end-of-turn and similar.
 */
export function playTap(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.05);
  const bp = ac.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1200;
  bp.Q.value = 1.5;
  const env = envelope(ac, t, 0.2, 0.002, 0.05);
  noise.connect(bp); bp.connect(env); env.connect(masterGain!);
  noise.start(t); noise.stop(t + 0.06);
}

/**
 * "Désapprouv" — short descending meow. Used for "you can't do that"
 * feedback (error, cancel, etc.).
 */
export function playMiauDesapprouv(): void {
  if (throttle('miau-desapprouv', 250)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(800, t);
  osc.frequency.exponentialRampToValueAtTime(360, t + 0.22);
  const env = envelope(ac, t, 0.28, 0.02, 0.2);
  osc.connect(env); env.connect(masterGain!);
  osc.start(t); osc.stop(t + 0.24);
}

/**
 * "Victoire" — 4-note triumphant melody. Used when the game ends with
 * the local player winning.
 */
export function playVictory(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const notes = [600, 900, 1200, 1800];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.16;
    const env = envelope(ac, start, 0.35, 0.01, 0.3);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.32);
  }
}

/**
 * "MyTurn" — a 3-note rising chime followed by a short steady note with
 * vibrato. Distinct from `playFanfare` (capture) and `playTinkle` (research)
 * so the player instantly recognises "it's my turn now". Slightly longer
 * than the other UI cues to grab attention.
 */
export function playMyTurn(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Three quick rising blips
  const blips = [880, 1175, 1568]; // A5 → D6 → G6 (open, cheerful)
  for (let i = 0; i < blips.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = blips[i]!;
    const start = t + i * 0.07;
    const env = envelope(ac, start, 0.3, 0.005, 0.1);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.12);
  }
  // Settled chord at the end — gives that "ready to act" feel
  const sustainStart = t + 0.25;
  for (const freq of [880, 1318]) { // A5 + E6 perfect fifth
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Tiny vibrato so the chord feels alive, not mechanical
    const lfo = ac.createOscillator();
    lfo.frequency.value = 5;
    const lfoGain = ac.createGain();
    lfoGain.gain.value = 4;
    lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
    const env = envelope(ac, sustainStart, 0.22, 0.02, 0.28);
    osc.connect(env); env.connect(masterGain!);
    osc.start(sustainStart); osc.stop(sustainStart + 0.32);
    lfo.start(sustainStart); lfo.stop(sustainStart + 0.32);
  }
}

/**
 * "Coussinet" — soft cat-paw pad step. Low-frequency sine "thump" mixed
 * with a low-passed noise tail for the muffled "padding on a rug" body.
 * Short (~120 ms) so it can stamp under combat / move outcomes without
 * stomping over them.
 */
export function playPawStep(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Low sine thump: 150 Hz → 70 Hz drop ≈ a soft beat.
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(70, t + 0.08);
  const oscEnv = envelope(ac, t, 0.22, 0.005, 0.10);
  osc.connect(oscEnv); oscEnv.connect(masterGain!);

  // Low-passed noise burst → the muffled "pad" texture.
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.08);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(420, t);
  lp.frequency.exponentialRampToValueAtTime(180, t + 0.08);
  lp.Q.value = 0.7;
  const noiseEnv = envelope(ac, t, 0.10, 0.003, 0.07);
  noise.connect(lp); lp.connect(noiseEnv); noiseEnv.connect(masterGain!);

  osc.start(t); noise.start(t);
  osc.stop(t + 0.14); noise.stop(t + 0.10);
}

/**
 * "Dice throw" — soft whoosh as the dice flies across the table. Lighter
 * and shorter than `playWhoosh` so the impact + reveal cues that follow
 * still pop.
 */
export function playDiceThrow(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.45);
  const bp = ac.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(2200, t);
  bp.frequency.exponentialRampToValueAtTime(700, t + 0.40);
  bp.Q.value = 3;
  const env = envelope(ac, t, 0.18, 0.04, 0.36);
  noise.connect(bp); bp.connect(env); env.connect(masterGain!);
  noise.start(t); noise.stop(t + 0.42);
}

/**
 * "Dice impact" — wood-on-wood "tock" when the dice first hits the table.
 * Square wave with a fast pitch drop + a brief noise burst for the grain.
 */
export function playDiceImpact(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Pitched click
  const osc = ac.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(300, t);
  osc.frequency.exponentialRampToValueAtTime(140, t + 0.04);
  const oscEnv = envelope(ac, t, 0.30, 0.002, 0.07);
  osc.connect(oscEnv); oscEnv.connect(masterGain!);
  // "Wood grain" high-passed noise burst
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.05);
  const hp = ac.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1200;
  const nEnv = envelope(ac, t, 0.18, 0.002, 0.045);
  noise.connect(hp); hp.connect(nEnv); nEnv.connect(masterGain!);
  osc.start(t); noise.start(t);
  osc.stop(t + 0.09); noise.stop(t + 0.06);
}

/**
 * "Dice bounce" — smaller, brighter rebound after the first impact. Used
 * for each subsequent bounce as the dice settles.
 */
export function playDiceBounce(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(480, t);
  osc.frequency.exponentialRampToValueAtTime(220, t + 0.03);
  const env = envelope(ac, t, 0.16, 0.002, 0.06);
  osc.connect(env); env.connect(masterGain!);
  osc.start(t); osc.stop(t + 0.07);
}

/**
 * "Dice reveal" — bright three-note arpeggio when the face number flips
 * from "?" to the real value. Sparkle + sense of discovery.
 */
export function playDiceReveal(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Major triad arpeggio — A5 / C#6 / E6
  const notes = [880, 1108, 1318];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.04;
    const env = envelope(ac, start, 0.22, 0.005, 0.22);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.24);
  }
  // Brief shimmer high-end for sparkle.
  const osc2 = ac.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(2200, t + 0.08);
  osc2.frequency.exponentialRampToValueAtTime(3200, t + 0.20);
  const env2 = envelope(ac, t + 0.08, 0.10, 0.005, 0.18);
  osc2.connect(env2); env2.connect(masterGain!);
  osc2.start(t + 0.08); osc2.stop(t + 0.27);
}

/**
 * "Sparkle" — bright shimmering pattern. Used for boost purchase
 * (consumable upgrades).
 */
export function playSparkle(): void {
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const notes = [1400, 1800, 2300, 2800, 1900];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.05;
    const env = envelope(ac, start, 0.18, 0.005, 0.12);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.14);
  }
}

/**
 * "Barbarian arrival" — three brutish growls (sawtooth, low) overlapping
 * with a war-horn rise (square sweep 180→320 Hz) and a noise crash for
 * the boat hitting the shore. Reads as "OUARGH ! pruuut ! BIM !".
 */
export function playBarbarianArrival(): void {
  if (throttle('barbarian-arrival', 1000)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  // Three angry meow-growls — sawtooth waves stepping in pitch.
  const growls = [220, 180, 260];
  for (let i = 0; i < growls.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = growls[i]!;
    // Tiny pitch wobble for menace.
    const lfo = ac.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 7;
    const lfoGain = ac.createGain();
    lfoGain.gain.value = 14;
    lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
    const start = t + i * 0.18;
    const env = envelope(ac, start, 0.32, 0.02, 0.32);
    const filter = ac.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1200;
    osc.connect(filter); filter.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.4);
    lfo.start(start); lfo.stop(start + 0.4);
  }
  // War-horn rise — square wave sweep, slow attack, long release.
  const horn = ac.createOscillator();
  horn.type = 'square';
  horn.frequency.setValueAtTime(180, t + 0.05);
  horn.frequency.linearRampToValueAtTime(320, t + 0.65);
  const hornEnv = envelope(ac, t + 0.05, 0.22, 0.05, 0.6);
  const hornFilter = ac.createBiquadFilter();
  hornFilter.type = 'lowpass';
  hornFilter.frequency.value = 900;
  horn.connect(hornFilter); hornFilter.connect(hornEnv); hornEnv.connect(masterGain!);
  horn.start(t + 0.05); horn.stop(t + 0.7);
  // Boat-hits-shore crash — a quick noise burst with low-pass.
  const noise = ac.createBufferSource();
  noise.buffer = buildNoiseBuffer(ac, 0.4);
  const noiseFilter = ac.createBiquadFilter();
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.value = 600;
  const noiseEnv = envelope(ac, t + 0.55, 0.45, 0.005, 0.35);
  noise.connect(noiseFilter); noiseFilter.connect(noiseEnv); noiseEnv.connect(masterGain!);
  noise.start(t + 0.55); noise.stop(t + 0.95);
}

/** Per-faction pitch ratio used by playMeowForFaction / playPurrForFaction.
 *  Maps each faction to a frequency multiplier so the same SFX skeleton
 *  reads as a different cat-personality. Defaults to 1.0 for unknown ids. */
const FACTION_PITCH: Record<string, number> = {
  astralina: 1.10,   // a touch ethereal
  baumdiala: 0.78,   // gross sewer cat → low
  diasardra: 0.92,
  doodalia:  1.18,   // soft fluffy
  dracalina: 0.70,   // vampire growl
  foratala:  1.05,
  mountiala: 0.85,
  niagigala: 1.22,   // icy and high
  ocianala:  0.96,
  planyala:  1.30,   // squeaky goofball
  robitala:  0.90,
  saliana:   0.83,
  sucratina: 1.40,   // candy chirp
  teloundra: 0.75,
  barbare:   0.65,   // very low growl
};

function pitchFor(factionId: string | null | undefined): number {
  if (!factionId) return 1.0;
  return FACTION_PITCH[factionId] ?? 1.0;
}

/** Faction-tinted "meow" used by recruit/attack notifications. Same
 *  envelope as playMeow but with the per-faction pitch ratio applied. */
export function playMeowForFaction(factionId: string | null | undefined): void {
  // Per-faction throttle so two different kingdoms can still meow at the
  // same instant — but the SAME faction recruiting twice in a tick
  // doesn't stack into a slurred double-meow.
  if (throttle(`meow-faction-${factionId ?? '_'}`, 200)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const p = pitchFor(factionId);
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(620 * p, t);
  osc.frequency.linearRampToValueAtTime(1120 * p, t + 0.10);
  osc.frequency.linearRampToValueAtTime(540 * p, t + 0.28);
  const vibrato = ac.createOscillator();
  vibrato.type = 'sine';
  vibrato.frequency.value = 12;
  const vGain = ac.createGain();
  vGain.gain.value = 40 * p;
  vibrato.connect(vGain); vGain.connect(osc.frequency);
  const env = envelope(ac, t, 0.28, 0.02, 0.26);
  osc.connect(env); env.connect(masterGain!);
  osc.start(t); osc.stop(t + 0.32);
  vibrato.start(t); vibrato.stop(t + 0.32);
}

/** "Battle won!" — short major-chord fanfare with faction pitch. */
export function playBattleWin(factionId: string | null | undefined): void {
  if (throttle(`battle-win-${factionId ?? '_'}`, 400)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const p = pitchFor(factionId);
  const notes = [523, 659, 784]; // C E G
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = notes[i]! * p;
    const start = t + i * 0.08;
    const env = envelope(ac, start, 0.22, 0.01, 0.22);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.25);
  }
}

/** "Battle lost" — descending minor wail, faction-tinted. */
export function playBattleLose(factionId: string | null | undefined): void {
  if (throttle(`battle-lose-${factionId ?? '_'}`, 400)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const p = pitchFor(factionId);
  const osc = ac.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(440 * p, t);
  osc.frequency.exponentialRampToValueAtTime(140 * p, t + 0.65);
  const env = envelope(ac, t, 0.24, 0.03, 0.7);
  const filt = ac.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.value = 1500;
  osc.connect(filt); filt.connect(env); env.connect(masterGain!);
  osc.start(t); osc.stop(t + 0.75);
}

/** "Camp built" — a quick wooden tap-tap-tap construction sound. */
export function playCampBuild(): void {
  if (throttle('camp-build', 300)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  for (let i = 0; i < 3; i++) {
    const osc = ac.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 220 - i * 30;
    const start = t + i * 0.08;
    const env = envelope(ac, start, 0.18, 0.005, 0.05);
    const filt = ac.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = 800;
    osc.connect(filt); filt.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.07);
  }
}

/** "Snacalia collected" — a cheerful triangle chirp on a fifth. */
export function playSnacaliaCollect(): void {
  if (throttle('snacalia-collect', 400)) return;
  const ac = getCtx(); if (!ac) return;
  const t = ac.currentTime;
  const notes = [880, 1318, 1760];
  for (let i = 0; i < notes.length; i++) {
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = notes[i]!;
    const start = t + i * 0.06;
    const env = envelope(ac, start, 0.22, 0.005, 0.14);
    osc.connect(env); env.connect(masterGain!);
    osc.start(start); osc.stop(start + 0.16);
  }
}
